import { Controller } from "@hotwired/stimulus";

export default class MapListController extends Controller {
  static targets = ["item", "list"];
  static outlets = ["map"]; // Direct communication with map controller
  static values = {
    mapControllerId: String, // ID of the map controller's element
  };

  initialize() {
    // Bind methods to preserve context
    this.handleMarkerClick = (event) => this.highlightListItem(event, true);
    this.handleMarkerMouseover = (event) =>
      this.highlightListItem(event, false);
    this.handleMarkerMouseout = this.resetListItemHighlight.bind(this);
  }

  connect() {
    this.mapElement = document.getElementById(this.mapControllerIdValue);

    // During Turbo page visits, permanent elements may not be in DOM yet.
    // Wait for Turbo to finish rendering before setting up.
    if (!this.mapElement) {
      this.handleTurboRender = () => {
        this.mapElement = document.getElementById(this.mapControllerIdValue);
        if (this.mapElement) {
          this.setupMapListeners();
        }
      };
      document.addEventListener("turbo:render", this.handleTurboRender, {
        once: true,
      });
      return;
    }

    this.setupMapListeners();
  }

  // Stimulus outlet callback - fires when map outlet becomes available
  // This is the PRIMARY coordination point for rendering markers
  mapOutletConnected(controller, element) {
    this.mapElement = element;

    // If map is already initialized (turbo-permanent), render markers now
    // Guard with hasRenderedInitial to prevent rezoom on outlet reconnect
    if (
      element.dataset.mapReady &&
      this.hasItemTarget &&
      !this.hasRenderedInitial
    ) {
      this.renderMarkers({ replace: true, fitMarkers: true });
    }
  }

  setupMapListeners() {
    if (!this.mapElement) return;

    // Add marker event listeners (these bubble from DOM, not outlet-convertible)
    this.mapElement.addEventListener(
      "map:marker:click",
      this.handleMarkerClick,
    );
    this.mapElement.addEventListener(
      "map:marker:mouseover",
      this.handleMarkerMouseover,
    );
    this.mapElement.addEventListener(
      "map:marker:mouseout",
      this.handleMarkerMouseout,
    );

    // map:upgraded handles static-to-interactive upgrade (different flow than navigation)
    this.mapElement.addEventListener(
      "map:upgraded",
      () => this.renderMarkers({ replace: true }),
      { once: true },
    );

    // Listen for Turbo Stream renders (backup to Stimulus target callbacks)
    // turbo:before-stream-render fires before each stream action is applied
    this.handleTurboStream = (event) => {
      const target = event.target.getAttribute("target");
      // Only react to results updates
      if (target === "results") {
        requestAnimationFrame(() => {
          this.scheduleMarkerUpdate({ forceReplace: true });
        });
      }
    };
    document.addEventListener(
      "turbo:before-stream-render",
      this.handleTurboStream,
    );
  }

  disconnect() {
    clearTimeout(this.markerUpdateTimeout);

    if (this.handleTurboRender) {
      document.removeEventListener("turbo:render", this.handleTurboRender);
    }
    if (this.handleTurboStream) {
      document.removeEventListener(
        "turbo:before-stream-render",
        this.handleTurboStream,
      );
    }

    if (this.mapElement) {
      this.mapElement.removeEventListener(
        "map:marker:click",
        this.handleMarkerClick,
      );
      this.mapElement.removeEventListener(
        "map:marker:mouseover",
        this.handleMarkerMouseover,
      );
      this.mapElement.removeEventListener(
        "map:marker:mouseout",
        this.handleMarkerMouseout,
      );
    }
  }

  // Callback from map_controller via outlet - called when map is ready
  // This is a fallback if mapOutletConnected fires before map:ready
  onMapReady() {
    // Only render if we have items and haven't already rendered via mapOutletConnected
    if (this.hasItemTarget && !this.hasRenderedInitial) {
      this.hasRenderedInitial = true;
      this.renderMarkers({ replace: true, fitMarkers: true });
    }
  }

  // Callback from map_controller via outlet - called when map upgrades from static
  onMapUpgraded() {
    this.renderMarkers({ replace: true });
  }

  // Stimulus target lifecycle callbacks - fire when items connect/disconnect from DOM
  // This handles both Turbo Frames and Turbo Streams updates
  itemTargetConnected(element) {
    this.hadConnects = true;
    this.scheduleMarkerUpdate();
  }

  itemTargetDisconnected(element) {
    // Track that items were removed - indicates filter/sort change, not Load More
    this.hadDisconnects = true;
    this.scheduleMarkerUpdate();
  }

  scheduleMarkerUpdate({ forceReplace = false } = {}) {
    // Debounce to coalesce rapid connect/disconnect cycles during DOM replacement
    clearTimeout(this.markerUpdateTimeout);
    // Track if any call requested forceReplace (e.g., from MutationObserver)
    if (forceReplace) this.pendingForceReplace = true;

    this.markerUpdateTimeout = setTimeout(() => {
      if (this.mapElement?.dataset.mapReady) {
        // Replace markers if: explicitly forced, or items were disconnected (filter/sort change)
        const shouldReplace = this.pendingForceReplace || this.hadDisconnects;
        this.hadConnects = false;
        this.hadDisconnects = false;
        this.pendingForceReplace = false;

        // Never fit markers for in-page updates (pagination, filters, sort)
        // Only initial load and navigation should fit markers (handled by mapOutletConnected/onMapReady)
        this.renderMarkers({ replace: shouldReplace });
      }
    }, 50);
  }

  renderMarkers({ replace = false, fitMarkers = false } = {}) {
    // Prevent stale renders - element removed from DOM during navigation
    if (!this.element.isConnected) return;
    if (!this.mapElement) return;

    // Track that we've done initial render (prevents duplicate via onMapReady)
    this.hasRenderedInitial = true;

    const markers = this.itemTargets.map((item) => ({
      lat: parseFloat(item.dataset.lat),
      lng: parseFloat(item.dataset.lng),
      label: item.dataset.markerLabel,
      itemId: item.dataset.itemId,
      // Callout data
      url: item.dataset.churchUrl,
      image: item.dataset.churchImage,
      denomination: item.dataset.churchDenomination,
      location: item.dataset.churchLocation,
    }));

    if (this.hasMapOutlet) {
      this.mapOutlet.addMarkers({ markers, replace, fitMarkers });
    }
  }

  highlightListItem(event, scroll = false) {
    const { itemId } = event.detail;
    const isMobile = window.innerWidth < 768;

    this.itemTargets.forEach((item) => {
      if (item.dataset.itemId === itemId) {
        item.classList.add("list-item--highlight");
        // Skip scroll on mobile - list may be hidden behind map
        if (scroll && !isMobile) {
          item.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });
  }

  resetListItemHighlight(event) {
    const { itemId } = event.detail;
    this.itemTargets.forEach((item) => {
      if (item.dataset.itemId === itemId) {
        item.classList.remove("list-item--highlight");
      }
    });
  }

  highlightMarker(event) {
    if (!this.mapElement) return;
    const itemId = event.currentTarget.dataset.itemId;

    if (this.hasMapOutlet) {
      this.mapOutlet.highlightMarker(itemId);
    }
  }

  resetHighlight(event) {
    if (!this.mapElement) return;
    const itemId = event.currentTarget.dataset.itemId;

    if (this.hasMapOutlet) {
      this.mapOutlet.resetMarkerHighlight(itemId);
    }
  }
}
